import { initializeApp, type FirebaseApp } from 'firebase/app';
import { LEAGUE } from '@ff/core';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  type Auth,
} from 'firebase/auth';

/**
 * Client Firebase config. These values are public by design (they identify the
 * project, they don't grant access — Firestore rules and the API allowlist do).
 * Pulled from Vite env so the same build points at the right project.
 *
 * Initialization is lazy: in mock/preview mode (VITE_USE_MOCK) the app never
 * touches Firebase, so importing this module must not initialize it — otherwise
 * a missing API key throws before the UI can render.
 */
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? LEAGUE.deployment.firebaseProjectId;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // Firebase gives every project <id>.firebaseapp.com, so the default is right
  // unless a custom auth domain has been set up.
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? `${projectId}.firebaseapp.com`,
  projectId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | undefined;
let authInstance: Auth | undefined;

export function getFirebaseAuth(): Auth {
  if (!authInstance) {
    app ??= initializeApp(firebaseConfig);
    authInstance = getAuth(app);
  }
  return authInstance;
}

const google = new GoogleAuthProvider();
google.setCustomParameters({ prompt: 'select_account' });

export function signInWithGoogle() {
  return signInWithPopup(getFirebaseAuth(), google);
}

export function signOut() {
  return fbSignOut(getFirebaseAuth());
}
